<script lang="ts">
import { browser } from '$app/environment'
import { page } from '$app/state'
import {
  STUDY_CHART_JS_GATE,
  type StudyChartJsGate,
} from '$lib/components/study/study-chart-js-gate'
import { baseChartOptions, getStudyChartThemeColors } from '$lib/utils/report/chart-config'
import { getContext, onMount } from 'svelte'

export interface BarDataset {
  data: number[]
  backgroundColor: string | string[]
  label?: string
  borderRadius?: number
}

interface Props {
  labels: string[]
  datasets: BarDataset[]
  yTickFormat?: (v: number) => string
  tooltipFormat?: (value: number, datasetLabel: string) => string
  /** Second line per bar in the tooltip (e.g. ICD code). Same length as labels when set. */
  tooltipExtraLines?: string[]
  /** Tooltip title per category (e.g. full diagnosis text when `labels` are short codes). */
  tooltipCategoryTitles?: string[]
  /** When true, categories are on the Y axis (better for long labels). */
  horizontal?: boolean
  showLegend?: boolean
  active?: boolean
  height?: string
}

let {
  labels,
  datasets,
  yTickFormat = (v) => String(v),
  tooltipFormat = (v) => String(v),
  tooltipExtraLines,
  tooltipCategoryTitles,
  horizontal = false,
  showLegend = false,
  active = false,
  height = '200px',
}: Props = $props()

let canvas: HTMLCanvasElement
let chartInstance: { destroy(): void } | null = null

const chartJsGate = getContext<StudyChartJsGate | undefined>(STUDY_CHART_JS_GATE)

/** Bumps on window resize so horizontal charts recompute label truncation vs. `innerWidth`. */
let chartLayoutTick = $state(0)

onMount(() => {
  if (!browser) return
  const update = () => {
    chartLayoutTick++
  }
  update()
  window.addEventListener('resize', update)
  return () => window.removeEventListener('resize', update)
})

$effect(() => {
  page.url.search
  void chartLayoutTick
  void chartJsGate?.ready
  if (!active || !canvas) return
  const Chart = (window as Window & { Chart?: unknown }).Chart as
    | { new (el: HTMLCanvasElement, cfg: object): { destroy(): void } }
    | undefined
  if (!Chart) return

  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }

  const host = (canvas.closest('.study-presentation') ?? canvas) as HTMLElement
  const th = getStudyChartThemeColors(host)

  const w = browser ? window.innerWidth : 1200
  const horizontalLabelMax =
    horizontal && w <= 430 ? 24 : horizontal && w <= 640 ? 32 : horizontal && w <= 900 ? 42 : 0
  const displayLabels =
    horizontalLabelMax > 0
      ? labels.map((s) =>
          s.length > horizontalLabelMax ? `${s.slice(0, horizontalLabelMax - 1)}…` : s,
        )
      : labels
  const categoryTickSize =
    horizontal && w <= 430 ? 9 : horizontal && w <= 640 ? 10 : horizontal ? 12 : undefined

  const useCustomTooltipTitle =
    (horizontal && horizontalLabelMax > 0) ||
    (tooltipCategoryTitles != null && tooltipCategoryTitles.length > 0)

  chartInstance = new Chart(canvas, {
    type: 'bar',
    data: {
      labels: displayLabels,
      datasets: datasets.map((ds) => ({
        data: ds.data,
        backgroundColor: ds.backgroundColor,
        label: ds.label ?? '',
        borderRadius: ds.borderRadius ?? 2,
      })),
    },
    options: {
      ...baseChartOptions,
      ...(horizontal ? { indexAxis: 'y' as const } : {}),
      plugins: {
        ...baseChartOptions.plugins,
        legend: showLegend
          ? {
              display: true,
              labels: {
                color: th.legend,
                boxWidth: 9,
                font: { size: 9 },
                padding: 8,
              },
            }
          : { display: false },
        tooltip: {
          ...baseChartOptions.plugins.tooltip,
          ...(horizontal
            ? {
                titleFont: { size: 12 },
                bodyFont: { size: 12 },
                padding: 10,
              }
            : {}),
          backgroundColor: th.tooltipBg,
          borderColor: th.tooltipBorder,
          titleColor: th.tooltipTitle,
          bodyColor: th.tooltipBody,
          callbacks: {
            ...(useCustomTooltipTitle
              ? {
                  title: (items: { dataIndex: number }[]) => {
                    const i = items[0]?.dataIndex
                    if (i === undefined) return ''
                    const fromTitles = tooltipCategoryTitles?.[i]
                    if (fromTitles != null && fromTitles !== '') return fromTitles
                    if (horizontal && horizontalLabelMax > 0) return labels[i] ?? ''
                    return ''
                  },
                }
              : {}),
            label: (c: { dataset: { label: string }; raw: number; dataIndex: number }) => {
              const base = tooltipFormat(c.raw, c.dataset.label ?? '')
              const extra = tooltipExtraLines?.[c.dataIndex]
              return extra ? `${base}\n${extra}` : base
            },
          },
        },
      },
      scales: {
        x: {
          ...baseChartOptions.scales.x,
          grid: { color: th.grid },
          ticks: {
            ...baseChartOptions.scales.x.ticks,
            color: th.tick,
            ...(horizontal
              ? {
                  callback: yTickFormat,
                  font: { size: 11 },
                }
              : {}),
          },
        },
        y: {
          ...baseChartOptions.scales.y,
          grid: { color: th.grid },
          ticks: {
            ...baseChartOptions.scales.y.ticks,
            color: th.tick,
            ...(!horizontal ? { callback: yTickFormat } : {}),
            ...(horizontal
              ? {
                  font: { size: categoryTickSize ?? 12 },
                  /** Long diagnosis labels: show full set, readable size */
                  autoSkip: false,
                }
              : {}),
          },
        },
      },
    },
  })

  return () => {
    if (chartInstance) {
      chartInstance.destroy()
      chartInstance = null
    }
  }
})
</script>

<div class="ch" style="height:{height}">
  <canvas bind:this={canvas}></canvas>
</div>
